// src/pages/cadastros/Procedimentos/NovoProcedimento.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { obterProximoCodigoSequencialProcedimento } from "./obterProximoCodigoSequencialProcedimento";
import { uploadToStorage } from "../../../utils/storageUpload";

function NovaProcedimento() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();

  const [maquinas, setMaquinas] = useState([]);
  const [carregandoMaquinas, setCarregandoMaquinas] = useState(false);

  const [setores, setSetores] = useState([]);
  const [setorIdSelecionado, setSetorIdSelecionado] = useState("");
  const [carregandoSetores, setCarregandoSetores] = useState(false);

  const [codigoGerado, setCodigoGerado] = useState("");
  const [maquinasSelecionadas, setMaquinasSelecionadas] = useState([]);
  const [descricao, setDescricao] = useState("");
  const [fotosTexto, setFotosTexto] = useState("");
  const [arquivosTexto, setArquivosTexto] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [enviandoFotos, setEnviandoFotos] = useState(false);
  const inputFotoCameraRef = useRef(null);
  const inputFotoArquivoRef = useRef(null);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  const fotosPreview = useMemo(() => {
    return (fotosTexto || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
  }, [fotosTexto]);

  async function handleEnviarFotos(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    setErro("");
    setSucesso("");

    const grupoId = usuarioLogado?.grupoId;
    if (!grupoId) {
      setErro("Usuário logado não possui grupo definido. Verifique o cadastro do Gestor.");
      return;
    }

    setEnviandoFotos(true);
    try {
      const empresaId = usuarioLogado?.empresaId || null;
      const novasUrls = [];

      for (const file of files) {
        const url = await uploadToStorage({
          grupoId,
          empresaId,
          pasta: "procedimentos/fotos",
          file,
          filenamePrefix: "procedimento_foto",
        });
        novasUrls.push(url);
      }

      setFotosTexto((prev) => {
        const linhas = String(prev || "")
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        for (const url of novasUrls) {
          if (!linhas.includes(url)) linhas.push(url);
        }
        return linhas.join("\n");
      });
    } catch (err) {
      console.error(err);
      setErro("Erro ao enviar foto. Verifique o Firebase Storage e tente novamente.");
    } finally {
      setEnviandoFotos(false);
      if (inputFotoCameraRef.current) inputFotoCameraRef.current.value = "";
      if (inputFotoArquivoRef.current) inputFotoArquivoRef.current.value = "";
    }
  }

  function removerFoto(url) {
    const u = String(url || "").trim();
    if (!u) return;
    setFotosTexto((prev) => {
      const linhas = String(prev || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .filter((x) => x !== u);
      return linhas.join("\n");
    });
  }

  useEffect(() => {
    async function carregarMaquinas() {
      if (!usuarioLogado?.grupoId) return;

      setCarregandoMaquinas(true);
      const grupoId = usuarioLogado.grupoId;
      const empresaId = usuarioLogado.empresaId || null;

      try {
        const maquinasRef = collection(db, "maquinas");
        let qM;

        if (empresaId) {
          qM = query(
            maquinasRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qM = query(maquinasRef, where("grupoId", "==", grupoId));
        }

        const snapM = await getDocs(qM);
        const listaM = snapM.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        listaM.sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", {
            sensitivity: "base",
          })
        );

        setMaquinas(listaM);
      } catch (e) {
        console.error("Erro ao carregar máquinas:", e);
        setErro((prev) =>
          prev ? prev + " / Erro ao carregar máquinas." : "Erro ao carregar máquinas."
        );
      } finally {
        setCarregandoMaquinas(false);
      }
    }

    carregarMaquinas();
  }, [usuarioLogado]);

  useEffect(() => {
    async function carregarSetores() {
      if (!usuarioLogado?.grupoId) return;

      setCarregandoSetores(true);
      const grupoId = usuarioLogado.grupoId;
      const empresaId = usuarioLogado.empresaId || null;

      try {
        const setoresRef = collection(db, "setores");
        let qS;

        if (empresaId) {
          qS = query(
            setoresRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qS = query(setoresRef, where("grupoId", "==", grupoId));
        }

        const snapS = await getDocs(qS);
        const listaS = snapS.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        listaS.sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", {
            sensitivity: "base",
          })
        );

        setSetores(listaS);
      } catch (e) {
        console.error("Erro ao carregar setores:", e);
        setErro((prev) =>
          prev ? prev + " / Erro ao carregar setores." : "Erro ao carregar setores."
        );
      } finally {
        setCarregandoSetores(false);
      }
    }

    carregarSetores();
  }, [usuarioLogado]);



  function handleChangeSetor(e) {
    const novoId = e.target.value;
    setSetorIdSelecionado(novoId);
    // evita salvar máquinas de outro setor por engano
    setMaquinasSelecionadas([]);
  }

  function handleChangeMaquinas(e) {
    const options = Array.from(e.target.options);
    const selecionadas = options
      .filter((opt) => opt.selected)
      .map((opt) => opt.value);
    setMaquinasSelecionadas(selecionadas);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }

    if (maquinasSelecionadas.length === 0) {
      setErro("Selecione pelo menos uma máquina.");
      return;
    }

    if (!descricao.trim()) {
      setErro("Informe a descrição do procedimento.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || usuarioLogado.grupo || null;
    const empresaId = usuarioLogado.empresaId || null;
    const empresaNome = usuarioLogado.empresaNome || null;

    const fotosArray = fotosTexto
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const arquivosArray = arquivosTexto
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const maquinasNomes = maquinasSelecionadas
      .map((id) => maquinas.find((m) => m.id === id)?.nome || "")
      .filter((n) => n);

    setCarregando(true);

    try {
      const sequencial = await obterProximoCodigoSequencialProcedimento(grupoId);
      const codigo = `PR${String(sequencial).padStart(4, "0")}`;

      const setorSelecionado = setores.find((s) => s.id === setorIdSelecionado) || null;

      const novaRef = doc(collection(db, "procedimentos"));
      await setDoc(novaRef, {
        codigo,
        codigoSequencial: sequencial,

        maquinasIds: maquinasSelecionadas,
        maquinasNomes,
        descricao: descricao.trim(),
        fotos: fotosArray.length ? fotosArray : [],
        arquivos: arquivosArray.length ? arquivosArray : [],
        observacoes: observacoes.trim() || null,

        grupoId,
        grupoNome: grupoNome || null,
        empresaId,
        empresaNome,

        criadoPor: usuarioLogado?.uid || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setCodigoGerado(codigo);
      setSucesso(`Procedimento criado com sucesso (código ${codigo}).`);

      setMaquinasSelecionadas([]);
      setDescricao("");
      setFotosTexto("");
      setArquivosTexto("");
      setObservacoes("");
    } catch (err) {
      console.error(err);
      setErro("Erro ao salvar procedimento. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const maquinasFiltradas = setorIdSelecionado
    ? maquinas.filter((m) => m.setorId === setorIdSelecionado)
    : maquinas;

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Novo Procedimento"
      subtitulo="Cadastros / Procedimentos / Novo"
    >
      <section style={{ width: "100%" }}>
        <div
          className="pc-card"
          style={{
            width: "100%",
            paddingLeft: 40,
            paddingRight: 40,
            boxSizing: "border-box",
          }}
        >
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Cadastro de procedimento</h3>
              <p>
                Cadastre procedimentos vinculados a uma ou mais máquinas.
              </p>
            </div>
          </header>

          {erro && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fecaca",
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#14532d",
                color: "#bbf7d0",
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: 12,
              maxWidth: 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* Código (somente info) */}
            <label>
              <span style={{ fontSize: 14 }}>Código</span>
              <input
                type="text"
                value={codigoGerado || "Será gerado automaticamente (PR0001...)"}
                readOnly
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  background: "#02061733",
                  color: "inherit",
                }}
              />
            </label>

            {/* Máquinas (multi-select) */}
            <label>
              <span style={{ fontSize: 14 }}>Máquinas</span>
              <select
                multiple
                value={maquinasSelecionadas}
                onChange={handleChangeMaquinas}
                disabled={carregandoMaquinas}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  color: "inherit",
                  minHeight: 120,
                }}
              >
                {carregandoMaquinas ? (
                  <option>Carregando máquinas...</option>
                ) : maquinas.length === 0 ? (
                  <option>Não há máquinas cadastradas.</option>
                ) : (
                  maquinas.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nome || "(sem nome)"}
                    </option>
                  ))
                )}
              </select>
              <small style={{ fontSize: 12, opacity: 0.8 }}>
                Segure CTRL (ou CMD) para selecionar várias.
              </small>
            </label>

            {/* Descrição */}
            <label>
              <span style={{ fontSize: 14 }}>Descrição</span>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Fotos */}
            <label>
              <span style={{ fontSize: 14 }}>Fotos</span>

              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  className="pc-btn-secundario"
                  disabled={enviandoFotos}
                  onClick={() => inputFotoCameraRef.current?.click()}
                >
                  📷 Tirar foto
                </button>

                <button
                  type="button"
                  className="pc-btn-secundario"
                  disabled={enviandoFotos}
                  onClick={() => inputFotoArquivoRef.current?.click()}
                >
                  ⬆️ Carregar foto
                </button>

                {enviandoFotos ? (
                  <small style={{ fontSize: 12, opacity: 0.85 }}>
                    Enviando...
                  </small>
                ) : null}
              </div>

              {/* Inputs escondidos */}
              <input
                ref={inputFotoCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleEnviarFotos(e.target.files)}
              />
              <input
                ref={inputFotoArquivoRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleEnviarFotos(e.target.files)}
              />

              {/* Preview */}
              {fotosPreview.length > 0 ? (
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {fotosPreview.map((url) => (
                    <div
                      key={url}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: 8,
                        borderRadius: 10,
                        border: "1px solid #334155",
                      }}
                    >
                      <img
                        src={url}
                        alt=""
                        style={{
                          width: 52,
                          height: 52,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #334155",
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 12, opacity: 0.9 }}
                        >
                          Abrir
                        </a>
                        <button
                          type="button"
                          className="pc-btn-perigo"
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          onClick={() => removerFoto(url)}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div style={{ marginTop: 10 }}>
                <small style={{ fontSize: 12, opacity: 0.85 }}>
                  (Opcional) Se preferir, você pode colar URLs abaixo (uma por linha):
                </small>
              </div>
              <textarea
                value={fotosTexto}
                onChange={(e) => setFotosTexto(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Arquivos */}
            <label>
              <span style={{ fontSize: 14 }}>
                Arquivos (URLs, uma por linha)
              </span>
              <textarea
                value={arquivosTexto}
                onChange={(e) => setArquivosTexto(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Observações */}
            <label>
              <span style={{ fontSize: 14 }}>Observações</span>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Ações */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/cadastros/procedimentos")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="pc-btn-primario"
                disabled={carregando || carregandoMaquinas}
              >
                {carregando ? "Salvando..." : "Salvar procedimento"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default NovaProcedimento;
